// ===================================================================
// AssistantsManager.tsx - Gestión de Asistentes para Profesionales
// ===================================================================

import React, { useState, useEffect } from 'react';
import {
  UserPlus,
  Users,
  Mail,
  Check,
  X,
  Clock,
  Trash2,
  AlertCircle
} from 'lucide-react';

interface Assistant {
  id: number;
  assistant_id: number;
  email: string;
  nombre: string;
  apellido: string;
  status: 'pending' | 'active' | 'revoked';
  permissions: {
    view_appointments: boolean;
    manage_appointments: boolean;
    view_patients: boolean;
    manage_patients: boolean;
  };
  invited_at: string;
  accepted_at: string | null;
}

interface AssistantsManagerProps {
  professionalId: number | undefined;
}

const AssistantsManager: React.FC<AssistantsManagerProps> = ({ professionalId }) => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form states
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    if (professionalId) {
      loadAssistants();
    }
  }, [professionalId]);

  const loadAssistants = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/assistants/my-assistants`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setAssistants(data.assistants);
      } else {
        setError(data.error || 'Error cargando asistentes');
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inviteEmail.trim()) {
      setError('Ingresa un email válido');
      return;
    }

    try {
      setInviting(true);
      setError('');
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/api/assistants/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: inviteEmail })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(`Invitación enviada a ${inviteEmail}`);
        setInviteEmail('');
        setShowInviteForm(false);
        loadAssistants();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Error enviando invitación');
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setInviting(false);
    }
  };

  const handleRevoke = async (assistantId: number) => {
    if (!window.confirm('¿Estás seguro de revocar el acceso a este asistente?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/api/assistants/revoke/${assistantId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Acceso revocado');
        loadAssistants();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Error revocando acceso');
      }
    } catch (err) {
      setError('Error de conexión');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <Check className="w-3 h-3" />
            Activo
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            <Clock className="w-3 h-3" />
            Pendiente
          </span>
        );
      case 'revoked':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <X className="w-3 h-3" />
            Revocado
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Gestión de Asistentes</h2>
          <p className="text-gray-600 text-sm mt-1">
            Invita a tu secretaria o asistente para que pueda ver y gestionar tu agenda
          </p>
        </div>
        <button
          onClick={() => setShowInviteForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <UserPlus className="w-5 h-5" />
          Invitar Asistente
        </button>
      </div>

      {/* Mensajes */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError('')} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center gap-2">
          <Check className="w-5 h-5" />
          {success}
        </div>
      )}

      

{/* Formulario de invitación */}
{showInviteForm && (
  <div className="mb-6 p-4 border border-blue-200 bg-blue-50 rounded-xl">
    <h3 className="font-medium text-gray-900 mb-3">Invitar nuevo asistente</h3>
    
    {/* 🔥 CAMBIO PRINCIPAL: Layout responsivo mejorado */}
    <form onSubmit={handleInvite} className="space-y-3 md:space-y-0">
      {/* En móvil: apilado vertical */}
      <div className="block md:hidden space-y-3">
        <input
          type="email"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          placeholder="correo@ejemplo.com"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
          required
          autoFocus
        />
        
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={inviting}
            className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {inviting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Enviando...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4" />
                Enviar
              </>
            )}
          </button>
          
          <button
            type="button"
            onClick={() => {
              setShowInviteForm(false);
              setInviteEmail('');
            }}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
          >
            Cancelar
          </button>
        </div>
      </div>
      
      {/* En desktop: layout horizontal */}
      <div className="hidden md:flex gap-2">
        <div className="flex-1">
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="Email del asistente"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
            autoFocus
          />
        </div>
        <button
          type="submit"
          disabled={inviting}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {inviting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Enviando...
            </>
          ) : (
            <>
              <Mail className="w-4 h-4" />
              Enviar Invitación
            </>
          )}
        </button>
        <button
          type="button"
          onClick={() => {
            setShowInviteForm(false);
            setInviteEmail('');
          }}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
        >
          Cancelar
        </button>
      </div>
    </form>
    
    <p className="text-sm text-gray-600 mt-2">
      El asistente recibirá una invitación y podrá aceptarla desde su cuenta.
    </p>
  </div>
)}

      {/* Lista de asistentes */}
      {assistants.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">Sin asistentes</h3>
          <p className="text-gray-600 mb-4">
            Aún no has invitado a ningún asistente a gestionar tu agenda
          </p>
          <button
            onClick={() => setShowInviteForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <UserPlus className="w-5 h-5" />
            Invitar Asistente
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {assistants.map((assistant) => (
            <div
              key={assistant.id}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-4">
                {/* ✅ ESTA ES LA PARTE CORREGIDA QUE EVITA EL CRASH 
                    Si no hay nombre, usa el email. Si no hay email, usa '?'
                */}
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-semibold text-lg">
                    {assistant.nombre 
                      ? assistant.nombre[0].toUpperCase() 
                      : (assistant.email ? assistant.email[0].toUpperCase() : '?')}
                  </span>
                </div>
                
                <div>
                  <p className="font-medium text-gray-900">
                    {assistant.nombre && assistant.apellido
                      ? `${assistant.nombre} ${assistant.apellido}`
                      : assistant.email}
                  </p>
                  <p className="text-sm text-gray-500">{assistant.email}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Invitado: {formatDate(assistant.invited_at)}
                    {assistant.accepted_at && ` • Aceptado: ${formatDate(assistant.accepted_at)}`}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {getStatusBadge(assistant.status)}
                
                {assistant.status === 'active' && (
                  <button
                    onClick={() => handleRevoke(assistant.assistant_id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Revocar acceso"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info de permisos */}
      <div className="mt-6 p-4 bg-gray-50 rounded-xl">
        <h4 className="font-medium text-gray-900 mb-2">¿Qué puede hacer un asistente?</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Ver tu agenda y citas programadas</li>
          <li>• Confirmar o cancelar turnos</li>
          <li>• Ver información básica de pacientes</li>
          <li>• <span className="text-red-600">No puede</span> emitir recetas electrónicas</li>
          <li>• <span className="text-red-600">No puede</span> acceder a historias clínicas completas</li>
        </ul>
      </div>
    </div>
  );
};

export default AssistantsManager;
